// Native mobile push (Capacitor). Web builds are untouched: every entry
// point checks Capacitor.isNativePlatform() first and the plugin is only
// loaded dynamically, so the website bundle never executes any of this.
//
// Flow: after login the app asks for permission, registers with FCM/APNs,
// and POSTs the device token to /api/push/register. The backend then pushes
// on new matches and chat messages with data.route ("/chat/<match_id>"),
// which we deep-link into when the user taps the notification.
import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";

let listenersBound = false;
let lastToken = null;

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

/** Register this device for push. Call after login (and on app resume).
 *  navigate: react-router navigate fn used for notification-tap deep links. */
export async function initPush(navigate) {
  if (!isNativeApp()) return;
  const { PushNotifications } = await import("@capacitor/push-notifications");

  if (!listenersBound) {
    listenersBound = true;

    await PushNotifications.addListener("registration", async ({ value }) => {
      lastToken = value;
      try {
        await api.post("/push/register", {
          token: value,
          platform: Capacitor.getPlatform(), // "ios" | "android"
        });
      } catch {
        // Best effort: a failed register just means no push until next login.
      }
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.warn("Push registration error", err);
    });

    // Notification tapped (app in background or killed): deep link.
    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      ({ notification }) => {
        const route = notification?.data?.route;
        if (route && typeof route === "string" && route.startsWith("/")) {
          navigate(route);
        }
      }
    );
  }

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== "granted") return;
  await PushNotifications.register();
}

/** Forget this device's token server side. Call on logout so a shared
 *  device stops receiving the previous user's matches and messages. */
export async function unregisterPush() {
  if (!isNativeApp() || !lastToken) return;
  try {
    await api.post("/push/unregister", { token: lastToken });
  } catch {
    // Cookie may already be gone; the dead token self-cleans on next send.
  }
  lastToken = null;
}
